import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  getDoc,
  getAggregateFromServer,
  sum,
  Timestamp,
} from "firebase/firestore";
import { useReactToPrint } from "react-to-print";

import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  MenuItem,
} from "@mui/material";

import ReplyIcon from "@mui/icons-material/Reply";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";

export default function CustomerOutstanding() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  const printRef = useRef();

  /* ================= STATE ================= */
  const [propertyId, setPropertyId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [payHistory, setPayHistory] = useState([]);

  const [searchText, setSearchText] = useState("");

  const [selectedCust, setSelectedCust] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [totalAmount, setTotalAmount] = useState(0);
  const [previousPaid, setPreviousPaid] = useState(0);
  const [currentPaid, setCurrentPaid] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [baseRemaining, setBaseRemaining] = useState(0);
  const [payType, setPayType] = useState("Cash");
  const [paidDate, setPaidDate] = useState(
    moment().format("YYYY-MM-DD")
  );

  /* ================= AUTH → PROPERTY ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      const snap = await getDoc(doc(db, "authroles", user.uid));
      if (snap.exists()) {
        setPropertyId(snap.data().propertyId);
      }
    });
    return () => unsub();
  }, []);

  /* ================= FETCH OUTSTANDING ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "restocustomers"),
      where("propertyId", "==", propertyId),
    );

    const unsub = onSnapshot(q, async (snap) => {
      const rows = [];

      for (const d of snap.docs) {
        const custId = d.id;

        const paidSnap = await getAggregateFromServer(
          query(collection(db, "customeracchistory"), where("custid", "==", custId)),
          { paid: sum("paid") }
        );

        const orderSnap = await getAggregateFromServer(
          query(collection(db, "orders"), where("customerid", "==", custId)),
          { total: sum("totalamount") }
        );

        const paid = paidSnap.data().paid || 0;
        const total = orderSnap.data().total || 0;
        const remaining = total - paid;

        if (remaining > 0) {
          rows.push({
            id: custId,
            data: d.data(),
            total,
            paid,
            remaining,
          });
        }
      }

      console.log(rows);
      setCustomers(rows);
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= PAYMENT ================= */
  const openPayment = (c) => {
    setSelectedCust(c);
    setTotalAmount(c.total);
    setPreviousPaid(c.paid);
    setCurrentPaid(0);
    setRemaining(c.remaining);
    setBaseRemaining(c.remaining);
    setPaymentOpen(true);
  };

  const calculateRemaining = (val) => {
    const r = baseRemaining - val;
    if (r < 0) {
      Toast.fire({ icon: "error", title: "Paid exceeds remaining" });
      return;
    }
    setRemaining(r);
  };

  const savePayment = async () => {
    await addDoc(collection(db, "customeracchistory"), {
      custid: selectedCust.id,
      paid: Number(currentPaid),
      remaining,
      paytype: payType,
      paiddate: paidDate,
      propertyId,
      note: "Customer outstanding payment",
      createdAt: Timestamp.now(),
    });

    Toast.fire({ icon: "success", title: "Payment saved" });
    setPaymentOpen(false);
  };

  /* ================= HISTORY ================= */
  const openHistory = (c) => {
    setSelectedCust(c);
    setHistoryOpen(true);

    const q = query(
      collection(db, "customeracchistory"),
      where("custid", "==", c.id),
      orderBy("paiddate", "desc")
    );

    onSnapshot(q, (snap) => {
      setPayHistory(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
    });
  };

  const deletePayment = async (id) => {
    await deleteDoc(doc(db, "customeracchistory", id));
    Toast.fire({ icon: "success", title: "Entry deleted" });
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  /* ================= UI ================= */
  return (
    <Box className=" p-3">
      <Card>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Customer Outstanding
          </Typography>

          <TextField
            size="small"
            placeholder="Search Customer"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ mb: 2, width: 300 }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    "#",
                    "Name",
                    "Mobile",
                    "Address",
                    "Total",
                    "Paid",
                    "Remaining",
                    // "Action",
                  ].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: "bold" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {customers
                  .filter((c) => {
                    if (!searchText) return true;
                    return (
                      c.data.name &&
                      c.data.name.toLowerCase().includes(searchText.toLowerCase())
                    );
                  })
                  .map((c, i) => (

                    <TableRow key={c.id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{c.data.name}</TableCell>
                      <TableCell>{c.data.mobile}</TableCell>
                      <TableCell>{c.data.address}</TableCell>
                      <TableCell>{c.total}</TableCell>
                      <TableCell>{c.paid}</TableCell>
                      <TableCell>{c.remaining}</TableCell>
                      {/* <TableCell>
                        <Tooltip title="Pay">
                          <IconButton
                            color="primary"
                            onClick={() => openPayment(c)}
                          >
                            <ReplyIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="View History">
                          <IconButton
                            color="success"
                            onClick={() => openHistory(c)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell> */}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ================= PAYMENT DIALOG ================= */}
      <Dialog open={paymentOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Pay Customer Amount</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <TextField fullWidth label="Total" value={totalAmount} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Previous Paid" value={previousPaid} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Current Paid"
                type="number"
                value={currentPaid}
                onChange={(e) => {
                  setCurrentPaid(e.target.value);
                  calculateRemaining(Number(e.target.value));
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Remaining" value={remaining} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Pay Type" value={payType} onChange={(e) => setPayType(e.target.value)}>
                {["Cash", "Bank", "RTGS", "Online"].map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="date" label="Paid Date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={savePayment}>
            Save
          </Button>
          <Button variant="outlined" onClick={() => setPaymentOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= HISTORY ================= */}
      <Dialog open={historyOpen} maxWidth="lg" fullWidth>
        <DialogTitle>Payment History</DialogTitle>
        <DialogContent ref={printRef}>
          <Table>
            <TableHead>
              <TableRow>
                {["Date", "Paid", "Remaining", "Type", "Action"].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payHistory.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{moment(p.paiddate).format("DD-MM-YYYY")}</TableCell>
                  <TableCell>{p.paid}</TableCell>
                  <TableCell>{p.remaining}</TableCell>
                  <TableCell>{p.paytype}</TableCell>
                  <TableCell>
                    <IconButton color="error" onClick={() => deletePayment(p.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<PrintIcon />} onClick={handlePrint}>
            Print
          </Button>
          <Button variant="outlined" onClick={() => setHistoryOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
